"use client";

import { useRef, useState } from "react";
import { uploadFileDirect, CreatorVoiceError } from "@/lib/creatorVoice/api";
import { useJob } from "@/lib/creatorVoice/useJob";
import { inputClass, ErrorBox, JobProgress, AudioResult } from "@/components/creatorVoice/CreatorVoiceUI";
import Spinner from "@/components/Spinner";

const PRESETS = [
  { id: "male_to_female", label: "Male → Female" },
  { id: "female_to_male", label: "Female → Male" },
  { id: "child_voice", label: "Child Voice" },
  { id: "deep_voice", label: "Deep Voice" },
  { id: "anime_voice", label: "Anime Voice" },
  { id: "radio_host", label: "Radio Host" },
];

export default function VoiceTransformTool() {
  const [file, setFile] = useState<File | null>(null);
  const [preset, setPreset] = useState("deep_voice");
  const [strength, setStrength] = useState(1.0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { job, submitting, error, run } = useJob();

  async function handleSubmit() {
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const audioUrl = await uploadFileDirect(file);
      await run("voice_transform", { audio_url: audioUrl, preset, strength });
    } catch (e) {
      setUploadError(e instanceof CreatorVoiceError ? e.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div>
      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Upload a WAV audio file</label>
      <div
        onClick={() => inputRef.current?.click()}
        className="mt-2 flex h-24 cursor-pointer items-center justify-center rounded-xl border border-dashed border-slate-300 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
      >
        {file ? file.name : "Click to choose a .wav file"}
        <input ref={inputRef} type="file" accept="audio/wav,audio/x-wav" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </div>

      <div className="mt-3">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Effect</label>
        <select value={preset} onChange={(e) => setPreset(e.target.value)} className={inputClass}>
          {PRESETS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-3 flex items-center gap-3">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Strength: {strength.toFixed(2)}</label>
        <input type="range" min={0} max={1} step={0.05} value={strength} onChange={(e) => setStrength(parseFloat(e.target.value))} className="flex-1" />
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={uploading || submitting || !file}
          className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:opacity-50"
        >
          {uploading || submitting ? <Spinner className="h-4 w-4" /> : null}
          {uploading ? "Uploading…" : submitting ? "Submitting…" : "Transform Voice"}
        </button>
      </div>

      {uploadError ? <ErrorBox message={uploadError} /> : null}
      {error ? <ErrorBox message={error} /> : null}
      <JobProgress job={job} />
      <AudioResult job={job} />
    </div>
  );
}
